/**
 * Program Registry — shared program-id → { name, Icon, color } lookup.
 *
 * KEYSTONE for the Orb-style account / transaction-detail redesign. Provides the
 * single source of truth for per-program ICONS + human-readable NAMES so the UI
 * no longer needs four drifted, name-only maps that used to live in:
 *   - src/utils/transactionTypeDetector.ts  (PROGRAM_NAME_MAP)   <- now delegates here
 *   - src/pages/TransactionDetails.tsx       (PROGRAM_NAMES)      <- registry is go-forward
 *   - src/pages/ProgramDetails.tsx           (PROGRAM_NAMES)      <- registry is go-forward
 *   - src/services/searchService.ts          (KNOWN_PROGRAMS)     <- registry is go-forward
 *
 * The entries below are the de-duplicated UNION of those four maps (native + SPL
 * + Metaplex + X1/XDEX programs). `getProgramMeta` NEVER returns undefined: unknown
 * ids fall back to a truncated label + a generic Cpu icon + neutral color.
 *
 * Design notes:
 *   - lucide-react components ONLY (same components already used by
 *     transactionTypeDetector's TRANSACTION_TYPES) — no new dependencies, no emoji.
 *   - `color` strings are the exact Tailwind text-color classes from that file's
 *     TRANSACTION_TYPES palette (copied, not imported, to avoid a runtime circular
 *     import: transactionTypeDetector imports getRegisteredProgramName from here).
 */

import {
  Repeat2,
  Landmark,
  Coins,
  Cpu,
  Vote,
  Lock,
  Database,
  Settings,
  MessageSquare,
  Gauge,
  Image as ImageIcon,
  type LucideIcon,
} from 'lucide-react'

export interface ProgramMeta {
  /** Human-readable program name (e.g. "SPL Token", "XDEX", "System"). */
  name: string
  /** lucide-react icon component. Render as `<Icon className="..." />`. */
  Icon: LucideIcon
  /** Tailwind text-color class string, sourced from the TRANSACTION_TYPES palette. */
  color: string
  /** Optional external logo URL for programs that ship brand art. */
  logoUrl?: string
  /**
   * Optional same-origin path (or URL) to a bundled Anchor IDL JSON for programs
   * whose IDL is published in source but NOT yet on-chain (e.g. Anchor 1.x made
   * the legacy on-chain IDL instruction opt-in until a program upgrade). The UI
   * renders this ONLY as a fallback when no on-chain IDL exists, and MUST label
   * it as published-source (never imply it is on-chain). @see idlSourceUrl
   */
  idlUrl?: string
  /** Optional human-facing URL of the public source the bundled IDL came from. */
  idlSourceUrl?: string
}

/**
 * Tailwind text-color classes copied verbatim from
 * `transactionTypeDetector.ts` -> TRANSACTION_TYPES[*].color. Kept as local
 * constants (not imported) to avoid a value-level circular import cycle.
 */
const COLOR = {
  system: 'text-blue-700 dark:text-blue-400',
  vote: 'text-purple-700 dark:text-purple-400',
  stake: 'text-green-700 dark:text-green-400',
  token: 'text-orange-700 dark:text-orange-400',
  nft: 'text-pink-700 dark:text-pink-400',
  defi: 'text-indigo-700 dark:text-indigo-400',
  oracle: 'text-cyan-700 dark:text-cyan-400',
  governance: 'text-teal-700 dark:text-teal-400',
  memo: 'text-gray-700 dark:text-gray-400',
  compute: 'text-yellow-700 dark:text-yellow-400',
  program: 'text-violet-700 dark:text-violet-400',
  /** Neutral gray used for the unknown-program fallback (TRANSACTION_TYPES.unknown.color). */
  neutral: 'text-gray-600 dark:text-gray-500',
} as const

/**
 * The FortiBlox mark, served from `public/`. Used as `logoUrl` for
 * FortiBlox-family programs so branded surfaces render the real logo (crisp
 * when scaled down) instead of a generic lucide glyph.
 */
const FORTIBLOX_MARK = '/fortiblox-512x512.png'

/**
 * Curated registry — the de-duplicated union of the four legacy maps.
 * Icon legend: Repeat2 = DEX/AMM/swap, Landmark = governance, Coins = token
 * programs, Cpu = generic program, Vote = vote, Lock = stake, Database = oracle,
 * Settings = system/config, MessageSquare = memo, Gauge = compute-budget,
 * ImageIcon = metadata/NFT.
 */
export const PROGRAM_REGISTRY: Record<string, ProgramMeta> = {
  // ---- Native programs ----
  '11111111111111111111111111111111': { name: 'System', Icon: Settings, color: COLOR.system },
  'Vote111111111111111111111111111111111111111': { name: 'Vote', Icon: Vote, color: COLOR.vote },
  'Stake11111111111111111111111111111111111111': { name: 'Stake', Icon: Lock, color: COLOR.stake },
  'Config1111111111111111111111111111111111111': { name: 'Config', Icon: Settings, color: COLOR.system },
  'ComputeBudget111111111111111111111111111111': { name: 'Compute Budget', Icon: Gauge, color: COLOR.compute },
  'BPFLoaderUpgradeab1e11111111111111111111111': { name: 'BPF Loader (Upgradeable)', Icon: Cpu, color: COLOR.program },
  'BPFLoader2111111111111111111111111111111111': { name: 'BPF Loader', Icon: Cpu, color: COLOR.program },

  // ---- SPL token programs ----
  'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA': { name: 'SPL Token', Icon: Coins, color: COLOR.token },
  'TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb': { name: 'Token-2022', Icon: Coins, color: COLOR.token },
  'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL': { name: 'ATA', Icon: Coins, color: COLOR.token },
  // ZK ElGamal proof program — verifies confidential-transfer proofs on chain
  // (present + executable on X1 mainnet). Native program, no on-chain metadata.
  'ZkE1Gama1Proof11111111111111111111111111111': { name: 'ZK ElGamal Proof', Icon: Lock, color: COLOR.governance },
  // Wrapped native mint (searchService KNOWN_PROGRAMS labels this "XNT" on X1).
  'So11111111111111111111111111111111111111112': { name: 'Wrapped XNT', Icon: Coins, color: COLOR.token },

  // ---- Metaplex / NFT ----
  'metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s': { name: 'Metaplex', Icon: ImageIcon, color: COLOR.nft },
  'hausS13jsjafwWwGqZTUQRmWyvyxn9EQpqMwV1PBBmk': { name: 'Auction House', Icon: ImageIcon, color: COLOR.nft },
  'cndy3Z4yapfJBmL3ShUp5exZKqR3z33thTzeNMm2gRZ': { name: 'Candy Machine', Icon: ImageIcon, color: COLOR.nft },

  // ---- DeFi / DEX (Repeat2) ----
  '675kPX9MHTjS2zt1qfr1NYHuzeLXfQM9H24wFSUt1Mp8': { name: 'Raydium', Icon: Repeat2, color: COLOR.defi },
  'RVKd61ztZW9GUwhRbbLoYVRE5Xf1B2tVscKqwZqXgEr': { name: 'Raydium', Icon: Repeat2, color: COLOR.defi },
  'whirLbMiicVdio4qvUfM5KAg6Ct8VwpYzGff3uctyCc': { name: 'Orca', Icon: Repeat2, color: COLOR.defi },
  '9xQeWvG816bUx9EPjHmaT23yvVM2ZWbrrpZb9PusVFin': { name: 'Serum', Icon: Repeat2, color: COLOR.defi },
  // X1 native DEX (XDEX) — mainnet + testnet program ids.
  'sEsYH97wqmfnkzHedjNcw3zyJdPvUmsa9AixhS4b4fN': { name: 'XDEX', Icon: Repeat2, color: COLOR.defi },
  'xdevKAYaAZFhACeWjNj7LQKy6dcWsTXmk7pz79qNhe6': { name: 'XDEX', Icon: Repeat2, color: COLOR.defi },
  // XenDEX (X1 ecosystem) — AMM + staking program ids (third-party; name only).
  '7EEuq61z9VKdkUzj7G36xGd7ncyz8KBtUwAWVjypYQHf': { name: 'XenDEX AMM', Icon: Repeat2, color: COLOR.defi },
  'E279H61mv8i4kc6P66HD8cSe4fApsEeZsf9rE1qxRPQc': { name: 'XenDEX Staking', Icon: Lock, color: COLOR.stake },

  // ---- FortiBlox family (our programs — branded with the FortiBlox mark) ----
  // logoUrl points at the FortiBlox mark served from public/. Branded surfaces
  // (program details header, search, program list) prefer logoUrl over `Icon`.
  '3geAsZiNaWDuTVtTbdWVQRitd55jY4UoWeeBmJFTJZmE': {
    name: 'FortiSwap Router',
    Icon: Repeat2,
    color: COLOR.defi,
    logoUrl: FORTIBLOX_MARK,
    // Published-source IDL, bundled in public/. Rendered as a labeled fallback
    // until the on-chain IDL is published in a future program upgrade.
    idlUrl: '/idl/fortiblox_router.json',
    idlSourceUrl: 'https://github.com/fortiblox/fortiblox-router',
  },
  'HbTbfPAnY2bT3zyo7x8Nw9mG4DMBDPLkpWgKzLGKB8qU': {
    name: 'FortiBlox Limit Orders',
    Icon: Repeat2,
    color: COLOR.defi,
    logoUrl: FORTIBLOX_MARK,
  },

  // ---- X1 ecosystem ----
  'CcgTMiYkgVfz7cAGkD6835BqfycG5N5Y4aPPHYW1EvKx': { name: 'X1 Oracle', Icon: Database, color: COLOR.oracle },
  'XPoo1Fx6KNgeAzFcq2dPTo95bWGUSj5KdPVqYj9CZux': { name: 'X1 Stake Pool', Icon: Lock, color: COLOR.stake },
  '8iq6zqaEVcfaym2u8t939PAN5jmfPVc6Z333RuxKTTZX': { name: 'Memo Minter', Icon: MessageSquare, color: COLOR.memo },

  // ---- Governance ----
  'GovER5Lthms3bLBqWub97yVrMmEogzX7xNjdXpPPCVZw': { name: 'Governance', Icon: Landmark, color: COLOR.governance },

  // ---- Memo ----
  'MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr': { name: 'Memo', Icon: MessageSquare, color: COLOR.memo },
  'Memo1UhkJRfHyvLMcVucJwxXeuD728EqVDDwQDxFMNo': { name: 'Memo', Icon: MessageSquare, color: COLOR.memo },
}

/** Fallback icon for unknown program ids. */
const FALLBACK_ICON: LucideIcon = Cpu

/**
 * Truncate a long base58 program id into a compact label, e.g.
 * `AbcdEf...` -> `Abcd…wXyz`. Short ids are returned unchanged.
 */
function truncateId(id: string): string {
  if (id.length <= 8) return id
  return `${id.slice(0, 4)}…${id.slice(-4)}`
}

/**
 * Resolve full display metadata for a program id. NEVER returns undefined:
 * unknown ids yield a truncated label + generic Cpu icon + neutral gray color.
 */
export function getProgramMeta(programId: string): ProgramMeta {
  const id = programId ?? ''
  const known = id ? PROGRAM_REGISTRY[id] : undefined
  if (known) return known
  return {
    name: id ? truncateId(id) : 'Unknown Program',
    Icon: FALLBACK_ICON,
    color: COLOR.neutral,
  }
}

/**
 * Convenience: the human-readable program name. Always returns a string
 * (falls back to the truncated id / "Unknown Program").
 */
export function getProgramName(programId: string): string {
  return getProgramMeta(programId).name
}

/**
 * Curated-only lookup: returns the registry name for KNOWN program ids, or
 * `null` for anything not in the registry. Used by legacy call sites (e.g.
 * transactionTypeDetector.getProgramName) that must preserve their
 * `string | null` contract rather than emit a truncated fallback.
 */
export function getRegisteredProgramName(programId: string | undefined | null): string | null {
  if (!programId) return null
  return PROGRAM_REGISTRY[programId]?.name ?? null
}
